<template>
    <RegularModal ref="modal" class="import-from-past-event-modal">
        <template v-slot:title>
            {{ translate("page.category_manager.import_categories") }}
        </template>
        <template v-slot:body>
            <div v-if="initDone" class="columns is-multiline">
                <div class="column is-12">
                    <div class="field">
                        <label class="label">{{ translate("page.category_manager.choose_event") }}</label>
                        <EventsSelect :events="events" v-model="selectedEventId" @open="eventSelectOpened" @close="eventSelectClosed" />
                    </div>
                </div>

                <div v-if="loadingCategories" class="column is-12">
                    <LoadingSpinner2 class="is-small" />
                </div>
                <template v-else-if="selectedEventId && !isEventSelectOpen">
                    <div v-if="categories.length > 0" class="column is-12">
                        <BranchNameField
                            v-if="isAppendMode"
                            ref="branchNameField"
                            v-model="branchName"
                            :new-branch-count="categories.length"
                            :show-error="showBranchNameError"
                        />
                        <template v-else>
                            <span>
                                {{ translate("page.category_manager.total_categories_to_import") }}
                                {{ categories.length }}
                            </span>
                            <div class="categories-listing">
                                <div class="p-1" v-for="category in categories" :key="category.key">
                                    {{ category.value }}
                                </div>
                            </div>
                        </template>
                    </div>
                    <div v-else class="column is-12">
                        <p>{{ translate("page.category_manager.no_categories_to_import") }}</p>
                    </div>
                </template>
            </div>
            <div v-else class="columns is-centered">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button @click="close" class="button">
                    {{ translate("common.cancel") }}
                </button>
                <LoadingButton @click="confirm" class="button is-primary" :disabled="!canSubmitImport" :is-loading="confirmInProgress">
                    {{ translate(isAppendMode ? "page.category_manager.append_branch_confirm" : "common.ok") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import RegularModal from "../Common/RegularModal.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import { computed, ref, watch } from "vue";
    import { handleError } from "../Common/errorHandler";
    import { translate } from "../Common/translations";
    import eventsApi from "../Api/events";
    import categoriesApi from "../Api/Categories/categories";
    import { useCategoriesManagerStore } from "./categoriesManagerStore";
    import EventsSelect from "../Common/Events/EventsSelect.vue";
    import type { CategoryKeyValue } from "../Api/Categories/categories";
    import type { CompactEvent } from "../Common/types";
    import { useModal } from "../Common/useModal";
    import BranchNameField from "./BranchNameField.vue";
    import { BRANCH_NAME_MAX_LENGTH } from "./branchName";

    const store = useCategoriesManagerStore();

    const initDone = ref(false);
    const selectedEventId = ref(0);
    const events = ref<CompactEvent[]>([]);
    const categories = ref<CategoryKeyValue[]>([]);
    const loadingCategories = ref(false);
    const confirmInProgress = ref(false);
    const branchName = ref("");
    const showBranchNameError = ref(false);
    const branchNameField = ref<{ errorKey: string | null } | null>(null);

    const eventId = computed(() => store.eventId);
    const isAppendMode = computed(() => store.rootNode !== null);
    const canSubmitImport = computed(
        () => selectedEventId.value > 0 && categories.value.length > 0 && (!isAppendMode.value || branchName.value.trim().length > 0),
    );

    const { modal, open: openModal, close: closeModal } = useModal();

    function refreshCategories() {
        return store.refreshCategories();
    }

    function open() {
        confirmInProgress.value = false;
        loadingCategories.value = false;
        initDone.value = false;
        selectedEventId.value = 0;
        categories.value = [];
        events.value = [];
        branchName.value = "";
        showBranchNameError.value = false;
        openModal();

        return fetchEventsForOrganizer()
            .then(() => {
                initDone.value = true;
            })
            .catch(handleError);
    }

    defineExpose({ open });

    function close() {
        closeModal();
    }

    function confirm() {
        if (isAppendMode.value && branchNameField.value && branchNameField.value.errorKey !== null) {
            showBranchNameError.value = true;
            return Promise.resolve();
        }

        confirmInProgress.value = true;
        return categoriesApi
            .importCategories(selectedEventId.value, eventId.value, isAppendMode.value ? branchName.value.trim() : undefined)
            .then(() => {
                return refreshCategories();
            })
            .then(() => {
                close();
            })
            .catch(handleError)
            .finally(() => {
                confirmInProgress.value = false;
            });
    }

    function fetchEventsForOrganizer() {
        return eventsApi.getEventsToManageForOrganizer(undefined, true).then(groupedEvents => {
            events.value = ([] as CompactEvent[])
                .concat(groupedEvents.incomingEvents, groupedEvents.draftEvents, groupedEvents.pastEvents)
                .sort((a, b) => {
                    const date1 = new Date(a.eventDate.startDate);
                    const date2 = new Date(b.eventDate.startDate);

                    return getTimestamp(date2) - getTimestamp(date1);
                })
                .filter(event => event.id !== eventId.value);
        });
    }

    watch(selectedEventId, onSelectedEvent);

    function onSelectedEvent() {
        showBranchNameError.value = false;

        if (selectedEventId.value < 1) {
            categories.value = [];
            branchName.value = "";
            return;
        }

        if (isAppendMode.value) {
            const selectedEvent = events.value.find(event => event.id === selectedEventId.value);
            branchName.value = translate("page.category_manager.append_branch_prefill_from_event", false, [
                selectedEvent?.fullName ?? "",
            ]).slice(0, BRANCH_NAME_MAX_LENGTH);
        }

        loadingCategories.value = true;
        return categoriesApi
            .getCategories(selectedEventId.value)
            .then(categoriesResponse => (categories.value = categoriesResponse))
            .catch(handleError)
            .finally(() => {
                loadingCategories.value = false;
            });
    }

    function getTimestamp(dateObj: Date) {
        return +dateObj; //Conversion to number happens here, so we get the timestamp
    }

    const isEventSelectOpen = ref(false);

    function eventSelectOpened() {
        isEventSelectOpen.value = true;
    }
    function eventSelectClosed() {
        isEventSelectOpen.value = false;
    }
</script>
